/**
 * @file This module represents the landing page of the application. The landing page contains an EBAF diagram banner,
 * informational headers, and primary action buttons - including one for account creation and another for downloading research.
 * This page serves as an introductory screen for users, giving them a brief overview of the application's purpose
 * and offering primary actions.
 */
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { alpha, styled } from '@mui/system';
import React, { useState } from 'react';
import banner from '../assets/hero-banner-landing-page.webp';
import backgroundImage from '../assets/landing-page-background.webp';
import { PageLayout } from './PageLayout';
import { useAuth0 } from '@auth0/auth0-react';
import { LegalCompliance } from '../components/text/LegalCompliance';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';

import PageLoader from '../components/PageLoader';

const CenteredGrid = styled(Grid)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    paddingRight: theme.spacing(2),
    paddingLeft: theme.spacing(2),
    paddingTop: theme.spacing(8),
    [theme.breakpoints.down('sm')]: {
        paddingBottom: theme.spacing(10) //give space for mobiles at bottom of screen for support widget
    }
}));

const StyledBanner = styled('img')(({ theme }) => ({
    width: '30rem',
    maxWidth: '100%',
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    paddingRight: theme.spacing(2),
    paddingLeft: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
        width: '21rem'
    },
    [theme.breakpoints.up('sm')]: {
        minWidth: '21rem'
    }
}));

const StyledTitleButton = styled(Button)(({ theme }) => ({
    height: '64px',
    width: '250px', // intentionally 'px' and not 'rem', prevents squishing of buttons during resize
    '&.Mui-disabled': {
        backgroundColor: theme.palette.disabled.darker
    },
    fontWeight: 600
}));

const StyledBackground = styled('div')(({ theme }) => ({
    backgroundColor: theme.palette.background.header,
    backgroundImage: `url(${backgroundImage})`,
    height: '100vh',
    backgroundRepeat: 'no-repeat',
    backgroundSize: 'cover',
    overflow: 'auto'
}));

const CenteredContainer = styled(Container)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '89vh',
    paddingRight: theme.spacing(2),
    paddingLeft: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
        paddingTop: theme.spacing(8) //prevent top getting cut off for mobile
    }
}));

/**
 * The `LandingPage` functional component represents the main landing page for the application.
 * It displays the EBAF diagram banner, titles, and action buttons.
 * It leverages authentication status to conditionally enable or disable the account creation option.
 *
 * @returns {ReactElement} The rendered landing page component.
 */
export const LandingPage = () => {
    const theme = useTheme();
    const { loginWithRedirect, isAuthenticated } = useAuth0();

    const [isLoading, setIsLoading] = useState(true);

    const isBelowSmallScreenSize = useMediaQuery((theme) =>
        theme.breakpoints.down('sm')
    );

    return (
        <React.Fragment>
            <div style={{ display: isLoading ? 'block' : 'none' }}>
                <PageLoader />
            </div>
            <div style={{ display: isLoading ? 'none' : 'block' }}>
                <PageLayout>
                    <StyledBackground>
                        <CenteredGrid container wrap={'nowrap'}>
                            <Grid item>
                                <CenteredContainer
                                    disableGutters
                                    component='main'
                                    maxWidth='ld'
                                    data-analyticsid='hero-banner'
                                >
                                    <Typography
                                        variant='header0'
                                        align='center'
                                        color={theme.palette.font.white}
                                        paragraph
                                    >
                                        Essentia Insight
                                    </Typography>
                                    <Typography
                                        variant='body'
                                        align='center'
                                        color={theme.palette.font.white}
                                        paragraph
                                    >
                                        Assessing active portfolio managers
                                        based on skill, <br /> not past
                                        performance
                                    </Typography>
                                    {isBelowSmallScreenSize && (
                                        <Box data-analyticsid='hero-image'>
                                            <StyledBanner
                                                src={banner}
                                                alt='Banner'
                                                onLoad={() =>
                                                    setIsLoading(false)
                                                }
                                            />
                                        </Box>
                                    )}
                                    <Grid
                                        container
                                        spacing={2}
                                        sx={{ justifyContent: 'center' }}
                                    >
                                        <Grid item>
                                            <StyledTitleButton
                                                variant='titleContained'
                                                onClick={() => {
                                                    loginWithRedirect({
                                                        authorizationParams: {
                                                            screen_hint:
                                                                'signup'
                                                        }
                                                    });
                                                }}
                                                //custom style for this button for hover-over behaviour
                                                sx={{
                                                    border: '2px solid',
                                                    borderColor:
                                                        theme.palette.orange
                                                            .main,
                                                    '&:hover': {
                                                        border: '2px solid',
                                                        borderColor:
                                                            theme.palette.common
                                                                .white
                                                    }
                                                }}
                                                disabled={isAuthenticated} // Disable the button if user is authenticated
                                                data-analyticsid='create-account'
                                            >
                                                Create a Free Account
                                            </StyledTitleButton>
                                        </Grid>
                                        <Grid item>
                                            <StyledTitleButton
                                                variant='titleOutlined'
                                                //custom style for this button for hover-over behaviour
                                                sx={{
                                                    backgroundColor:
                                                        theme.palette.common
                                                            .white,
                                                    '&:hover': {
                                                        backgroundColor: alpha(
                                                            theme.palette.common
                                                                .white,
                                                            0
                                                        )
                                                    }
                                                }}
                                                href='https://www.essentia-analytics.com/behavioral-alpha-benchmark-paper/'
                                                data-analyticsid='download-research'
                                            >
                                                Download the Research
                                            </StyledTitleButton>
                                        </Grid>
                                    </Grid>
                                    <Box marginTop={2}>
                                        <LegalCompliance />
                                    </Box>
                                </CenteredContainer>
                            </Grid>
                            {!isBelowSmallScreenSize && (
                                <Grid item>
                                    <StyledBanner
                                        src={banner}
                                        alt='Banner'
                                        onLoad={() => setIsLoading(false)}
                                    />
                                </Grid>
                            )}
                        </CenteredGrid>
                    </StyledBackground>
                </PageLayout>
            </div>
        </React.Fragment>
    );
};
